import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { DateRangeFilter } from "@/shared/DateRangeFilter";
import { TextFieldSearchFilter } from "@/shared/TextFieldSearchFilter";
import {
  dxtToLocalServerTime,
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import { GridApi, RowSelectedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Group, Panel } from "react-resizable-panels";
import { useResizablePanels } from "@/pages/sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "@/pages/sy/components/ResizablePanelSeparator";
import { Charge } from "@/pages/sy/models/Charge";
import { useChargeByIdQuery } from "@/pages/sy/hooks/queries/useChargeByIdQuery";
import { useFilteredChargesQuery } from "@/pages/sy/hooks/queries/useFilteredChargesQuery";
import { ActualRecipeChart } from "./components/ActualRecipeChart";
import { useTableData } from "./hooks/useTableData";

const moment = getMomentTz();

export const ScrapLayeringReportPage = () => {
  const { state } = useUTLSettingsContext();
  const refreshTime = parseInt(
    get(state.settings, "SCRAP_LAYERING_REPORT_REFRESH_TIME", 10),
  );

  const navigate = useNavigate();
  const { chargeId } = useParams<{ chargeId: string }>();

  // Resizable panels
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "scrap-layering-report-panels",
      defaultLayout: { table: 50, chart: 50 },
    });

  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  // Date filter states
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [selectedDays, setSelectedDays] = useState(1);

  // Adapt the date filter to the charge from the URL, once, on first load
  const initialChargeIdRef = useRef(chargeId ? parseInt(chargeId, 10) : null);
  const [urlChargeHandled, setUrlChargeHandled] = useState(
    initialChargeIdRef.current === null,
  );
  const { data: urlCharge } = useChargeByIdQuery(
    urlChargeHandled ? null : initialChargeIdRef.current,
  );

  useEffect(() => {
    if (urlChargeHandled || urlCharge === undefined) return;

    if (urlCharge) {
      const creationDay = dxtToLocalServerTime(
        urlCharge.CreationTimestamp,
        "yyyy-MM-dd",
      );
      if (creationDay) {
        const day = moment(creationDay, "YYYY-MM-DD");
        if (!selectedDate.isSame(day, "day")) {
          setSelectedDate(day);
        }
      }
    }
    setUrlChargeHandled(true);
  }, [urlCharge, urlChargeHandled]);

  const startDate = selectedDate
    .clone()
    .subtract(selectedDays, "days")
    .startOf("day")
    .toDate();
  const endDate = selectedDate
    .clone()
    .add(selectedDays, "days")
    .startOf("day")
    .toDate();

  const { data: charges = [], isLoading: isLoadingCharges } =
    useFilteredChargesQuery(startDate, endDate, 0);

  const { columnDefs } = useTableData();

  const handleDateChange = (newValue: moment.Moment | null) => {
    const dateToSet = newValue || moment();
    if (!selectedDate.isSame(dateToSet, "day")) {
      setSelectedDate(dateToSet);
    }
  };

  const handleDaysChange = (newDays: number) => {
    if (newDays !== selectedDays) {
      setSelectedDays(newDays);
    }
  };

  const handleNavigate = (direction: "prev" | "next") => {
    const daysToMove = direction === "next" ? selectedDays : -selectedDays;
    const newDate = selectedDate.clone().add(daysToMove, "days");
    setSelectedDate(newDate);
  };

  const onRowSelected = (event: RowSelectedEvent) => {
    if (event.node.isSelected()) {
      const charge = event.data as Charge;
      setSelectedCharge(charge);
      navigate(`/scrap-layering-report/${charge.ChargeId}`);
    }
  };

  // No useEffect needed for date/charge changes — TanStack Query re-fetches
  // automatically when query key params change (selectedCharge, dates).

  useEffect(() => {
    if (chargeId && charges.length > 0 && gridApi) {
      const chargeIdNum = parseInt(chargeId, 10);
      const charge = charges.find((c) => c.ChargeId === chargeIdNum);

      if (charge) {
        const rowNode = gridApi.getRowNode(String(charge.ChargeId));
        if (rowNode) {
          rowNode.setSelected(true);
          gridApi.ensureNodeVisible(rowNode);
        }

        if (selectedCharge?.ChargeId !== chargeIdNum) {
          setSelectedCharge(charge);
        }
      }
    }
  }, [chargeId, charges.length, gridApi]);

  return (
    <>
      <HelmetDexteel title="Scrap Layering Report" />
      <Box sx={{ p: 2 }} onContextMenu={(e) => e.preventDefault()}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Scrap Layering Report
        </Typography>

        <Group
          id="scrap-layering-report-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 140px)" }}
        >
          <Panel id="table" minSize={20}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <DateRangeFilter
                    selectedDate={selectedDate}
                    selectedDays={selectedDays}
                    onDateChange={handleDateChange}
                    onDaysChange={handleDaysChange}
                    onNavigate={handleNavigate}
                  />

                  <Grid item xs>
                    <TextFieldSearchFilter
                      value={filterValue}
                      onChange={(value: string) => {
                        setFilterValue(value);
                        gridApi?.setGridOption("quickFilterText", value);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Box
                sx={{
                  flex: 1,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <AgGridReact<Charge>
                  rowData={charges}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onGridReady={(params) => setGridApi(params.api)}
                  getRowId={(params) => String(params.data.ChargeId)}
                  onRowSelected={onRowSelected}
                  quickFilterText={filterValue}
                  includeHiddenColumnsInQuickFilter={true}
                  loading={isLoadingCharges}
                  {...defaultGridOptions}
                />
              </Box>
            </Stack>
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          <Panel id="chart" minSize={25}>
            <Paper
              elevation={1}
              sx={{
                height: "100%",
                overflow: "auto",
              }}
            >
              <ActualRecipeChart
                selectedCharge={selectedCharge}
                refetchInterval={refreshTime}
              />
            </Paper>
          </Panel>
        </Group>

        <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
      </Box>
    </>
  );
};
